import CoachMarks from '@maersk-global/coach-marks';

/**
 * Options for a single coach mark item.
 */
export interface CoachMarkItemOptions {
    /** Bold heading shown above the body text. */
    title?: string;
    /** Main descriptive text for the coach mark (required). */
    text: string;
    /**
     * The target element inside the host's shadow root.
     * Accepts either a CSS selector string (resolved against `shadowRoot`) or a
     * direct HTMLElement reference when you already have it.
     */
    target: string | HTMLElement;
    /**
     * Optional CSS selector for the inner part to use for positioning when the
     * target element has no layout box of its own (e.g. mc-button whose visual
     * button lives inside its shadow root as `part="button"`).
     * Accepts a CSS selector relative to `target.shadowRoot`, or a direct element.
     * If omitted the utility tries `[part="button"]` automatically.
     */
    anchorOverride?: string | HTMLElement;
    /** Called once the user has seen this coach mark step. */
    seen?: () => void;
}

/**
 * Options for `showCoachMark`.
 */
export interface ShowCoachMarkOptions {
    /**
     * The shadow root of the LitElement (or any web component) that contains
     * the target element. Pass `this.shadowRoot`.
     */
    debug?: boolean;
    shadowRoot: ShadowRoot;
    /** One or more coach mark steps to show in sequence. */
    items: CoachMarkItemOptions[];
    /**
     * Visual theme.
     * @default 'dark'
     */
    theme?: 'light' | 'dark';
    /**
     * Vertical gap in pixels between the target element and the coach mark popup.
     * @default 12
     */
    targetOffset?: number;
    /**
     * Horizontal margin in pixels to keep the popup away from viewport edges.
     * @default 30
     */
    coachMargin?: number;
    /**
     * Optional CSS class name applied to the CSS highlight ring on each target.
     * Defaults to the library default ('highlight').
     */
    highlightClass?: string;
}

// ─── Internal helpers ────────────────────────────────────────────────────────

/**
 * Resolves a target value to an HTMLElement.
 * When `value` is a string it is queried against `root`; otherwise it is
 * returned as-is.
 */
function resolveEl(root: ShadowRoot | HTMLElement, value: string | HTMLElement): HTMLElement | null {
    if (typeof value === 'string') {
        return root.querySelector<HTMLElement>(value) ?? null;
    }
    return value;
}

/**
 * Finds a bounding element that actually has a layout box.
 *
 * Problem: web components like mc-button use `display:contents` on the host,
 * meaning `getBoundingClientRect()` returns zeros. The library uses these
 * coordinates for positioning, producing `top:0, left:0`.
 *
 * Resolution order:
 *  1. The target itself, if it already has a real bounding box (plain HTML elements).
 *  2. The `anchorOverride` element/selector if provided.
 *  3. `[part="button"]` inside the target's shadow root (mc-button pattern).
 *  4. Any `[slot]` child inside the target (slotted icon content).
 *  5. The first ancestor in the light DOM with a non-zero bounding box.
 *  6. The target itself as last resort.
 */
function resolveAnchor(target: HTMLElement, anchorOverride?: string | HTMLElement): HTMLElement {
    // 1. Explicit override (a selector relative to target.shadowRoot, or element)
    if (anchorOverride) {
        const el = typeof anchorOverride === 'string'
            ? target.shadowRoot?.querySelector<HTMLElement>(anchorOverride)
            : anchorOverride;
        if (el) return el;
    }

    // 2. If the target itself has a real bounding box, use it directly.
    //    This is the common case for plain HTML elements (button, div, input, etc.).
    //    The fallbacks below are only needed when the target reports zero dimensions,
    //    e.g. mc-button which uses display:contents on its host element.
    const targetRect = target.getBoundingClientRect();
    if (targetRect.width > 0 || targetRect.height > 0) return target;

    // 3. Common mc-button pattern: inner [part="button"] in its own shadow root
    const partBtn = target.shadowRoot?.querySelector<HTMLElement>('[part="button"]');
    if (partBtn) return partBtn;

    // 4. Slotted icon child
    const slotted = target.querySelector<HTMLElement>('[slot]');
    if (slotted) return slotted;

    // 5. Walk up to first ancestor with a real bounding box
    let el: HTMLElement | null = target.parentElement;
    while (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 || r.height > 0) return el;
        el = el.parentElement;
    }

    return target;
}

/**
 * Corrects the position of the `.coach` popup after the library has placed it
 * incorrectly (because the target element reported zero dimensions).
 *
 * Called inside a `requestAnimationFrame` so the coach div is already in the
 * DOM but has not yet been painted.
 */
function repositionCoachMark(
    anchor: HTMLElement,
    cm: any,
): void {
    const coachEl = document.querySelector('.coach') as HTMLElement | null;
    if (!coachEl) return;

    let rect = anchor.getBoundingClientRect();

    // Last-chance fallback: nothing usable found yet
    if (rect.width === 0 && rect.height === 0) return;

    const coachWidth  = coachEl.offsetWidth  || 300;
    const targetOffset: number = cm.options?.targetOffset ?? 12;
    const margin: number       = cm.options?.coachMargin  ?? 30;

    const top  = rect.bottom + targetOffset;
    let   left = rect.left + rect.width / 2 - coachWidth / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - coachWidth - margin));

    coachEl.style.top      = `${top}px`;
    coachEl.style.left     = `${left}px`;
    coachEl.style.position = 'fixed';

    // Realign the arrow pointer to the centre of the target
    const arrow = coachEl.querySelector('.coach__arrow') as HTMLElement | null;
    if (arrow) {
        arrow.style.left  = `${rect.left + rect.width / 2 - left}px`;
        arrow.style.right = 'auto';
    }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Shows a sequence of coach marks for elements living inside one or more
 * shadow roots, working around two limitations of the `@maersk-global/coach-marks`
 * library:
 *
 * **1. Nested shadow DOM** — The library's `resolveTarget` calls
 * `document.querySelector()` which cannot pierce shadow roots at any depth.
 * This utility resolves elements directly from `shadowRoot` and patches the
 * instance method so the library uses our reference.
 *
 * **2. Zero-dimension host elements** — Web components like `mc-button` use
 * `display:contents` on the host, so `getBoundingClientRect()` returns zeros.
 * The coach mark ends up at `top:12, left:0`. After `start()` we reposition it
 * in a `requestAnimationFrame` using the first ancestor/inner-part that has a
 * real layout box.
 *
 * ---
 *
 * ### Basic usage (inside a LitElement)
 *
 * ```typescript
 * import { showCoachMark } from '../../../src/utils/coach-marks-utils';
 *
 * // In updated() or after updateComplete:
 * showCoachMark({
 *     shadowRoot: this.shadowRoot!,
 *     items: [{
 *         title: 'My Feature',
 *         text:  'Click here to use the new feature.',
 *         target: '#my-button',       // CSS selector inside this.shadowRoot
 *         seen: () => { /* persist seen state *\/ },
 *     }],
 * });
 * ```
 *
 * ### With an explicit anchor override (for deeply nested mc-button parts)
 *
 * ```typescript
 * showCoachMark({
 *     shadowRoot: this.shadowRoot!,
 *     items: [{
 *         text:           'Request a new ETA from the terminal.',
 *         target:         '#eta-request-btn',
 *         anchorOverride: '[part="button"]',   // resolved against target.shadowRoot
 *     }],
 *     theme: 'dark',
 * });
 * ```
 *
 * ### Storing the instance to hide it later
 *
 * ```typescript
 * private _coach: ReturnType<typeof showCoachMark> | null = null;
 *
 * this._coach = showCoachMark({ shadowRoot: this.shadowRoot!, items: [...] });
 *
 * // To dismiss programmatically:
 * this._coach?.hide();
 * ```
 *
 * @returns The `CoachMarks` instance so callers can call `.hide()` if needed.
 */
export function showCoachMark(opts: ShowCoachMarkOptions): InstanceType<typeof CoachMarks> {
    const { shadowRoot, items, theme = 'dark', targetOffset, coachMargin, highlightClass, debug = false } = opts;

    // Resolve all target elements up-front from within the shadow root.
    // The library normally calls document.querySelector which cannot cross
    // shadow boundaries, so we resolve them ourselves and patch resolveTarget.
    const resolvedItems = items.map(item => {
        const targetEl = resolveEl(shadowRoot, item.target);
        if (!targetEl) {
            console.warn('[CoachMark] Target not found:', item.target);
        }
        const anchorEl = targetEl ? resolveAnchor(targetEl, item.anchorOverride) : null;
        return { ...item, targetEl, anchorEl };
    });

    const libraryItems = resolvedItems.map(({ text, title, seen }) => ({
        text,
        title,
        selector: '__shadow__', // placeholder — overridden by resolveTarget patch
        seen,
    }));

    const coachMarkOptions: Record<string, unknown> = {
        mode: 'standard',
        theme,
        debug,
        items: libraryItems,
    };
    if (targetOffset !== undefined) coachMarkOptions['targetOffset'] = targetOffset;
    if (coachMargin  !== undefined) coachMarkOptions['coachMargin']  = coachMargin;
    if (highlightClass !== undefined) coachMarkOptions['highlightClass'] = highlightClass;

    const instance = new CoachMarks(coachMarkOptions as any);
    const cm = instance as any;

    // ── Patch 1: resolveTarget ─────────────────────────────────────────────
    // Return the pre-resolved host element so the library can apply the
    // highlight class to it. The caller is responsible for styling that class
    // (e.g. `#my-btn.highlight::part(button) { box-shadow: ... }` inside the
    // component's static styles since external CSS cannot pierce shadow roots).
    cm.resolveTarget = (_item: any) => {
        const idx = libraryItems.indexOf(_item);
        return resolvedItems[idx]?.targetEl ?? null;
    };

    // ── Patch 2: scrollerParent ────────────────────────────────────────────
    // Without this the library walks up through the shadow DOM, finds a
    // scrollable container inside a shadow root, and appends the `.coach` div
    // there — where `document.head` styles cannot reach it.
    // Returning null forces the div onto `document.body` (light DOM).
    cm.scrollerParent = () => null;

    instance.start();

    // ── Post-start reposition ──────────────────────────────────────────────
    // The library calculates position synchronously during start() using the
    // host element's (zero) bounding box. We correct this in rAF, after the
    // `.coach` div is in the DOM but before the browser paints.
    requestAnimationFrame(() => {
        const currentIdx: number = cm.current ?? 0;
        const anchor = resolvedItems[currentIdx]?.anchorEl;
        if (anchor) repositionCoachMark(anchor, cm);
    });

    return instance;
}
